/**
 * QUANTUM TERMINAL — single source of design tokens.
 *
 * Replaces the five parallel sources found in the audit (§4.1):
 *   A: theme/glasswork.js (THEME), theme/glasswork.css (--gw-*), theme/obsidian.css (--color-*)
 *   B: shared/theme.ts (theme object), Design/tokens.css
 *
 * Rules enforced here:
 *  - Every colour is an opaque hex unless it is deliberately a wash/overlay (audit §4.2 note 2:
 *    alpha-on-white text "floats" over flat s1/s2/s3, so text is solid hex).
 *  - JS reads these objects; CSS reads the variables emitted by cssVars(). No third copy.
 *  - Nothing in a component may hardcode a colour. Light theme later = one extra block below.
 *
 * Emission: `writeFileSync('tokens.css', cssVars())` in a prebuild step, or inject at boot.
 */

/* ─────────────────────────────────────────────────────────────── PALETTE ── */

export const palette = {
  /* Core — identical in both projects already, taken as-is (audit §4.1 key finding). */
  void: '#05070E', // window background, behind all panels
  bull: '#2CF5A7', // price up / bid / long — brand green, unchanged
  bear: '#FF5C87', // price down / ask / short
  warn: '#FFB84D', // attention, alerts, stop orders
  violet: '#8B7CFF', // ideas / situations / AI-derived content
  info: '#4DC3FF', // neutral data series (chart indicator lines) — owner 24.09.2026: more line hues than hi/warn/mid/low

  /* Surfaces — flat and opaque. cTrader/Tiger model, not glass (audit §4.5 "conflict of form"). */
  s1: '#0A0E18', // dock background / gutters between panels
  s2: '#111726', // panel body
  s3: '#18202F', // nested card, input, table header
  s4: '#1E2740', // hover row / selected row — new, audit §4.11 draft

  /* Lines — 1px, three weights. */
  line: '#1B2233', // hairline inside a panel (table rules)
  line2: '#2A3346', // panel border, control border
  line3: '#3A465E', // emphasised border: focus-adjacent, splitter hover

  /* Text — solid hex on every surface. Contrast verified on s1…s3 (see §a11y in doc). */
  hi: '#E6EAF2', // primary: numbers, values             — 14.8:1 on s2
  mid: '#8C96AC', // secondary: labels, column heads      —  5.6:1 on s2
  low: '#566079', // tertiary: units, timestamps          —  2.9:1 — never for body copy
  dis: '#3A4256', // disabled

  /* Interactive accent — the brand green. D1 (audit §8 q1) had split this to a
     cold blue so "selected" ≠ "price up"; owner reversed it 2026-09-10
     ("вернёмся к нашему зелёному, а то там уже какой-то синий"). Chrome and
     bull share the hue again, by decision. */
  acc: '#2CF5A7',
  accHi: '#6BFFCB', // hover / active text on accent
  accDeep: '#0F3D2C', // accent-tinted fill for selected rows

  /* Liquidation — deliberately NOT bear. Bear is "the market went down";
     liquidation is "you are about to be gone". Hotter and darker. */
  liq: '#FF3A2E',

  /* Structural levels — quiet by design: they are context, not signal. */
  level: '#5B6B8C',

  /* Deep fills (badge/chip backgrounds) — carried over from B's *Deep set. */
  bullDeep: '#0F3D2C',
  bearDeep: '#45182A',
  warnDeep: '#3D2E14',
  violetDeep: '#241E4A',
  accDeepBg: '#0F3D2C',
  neutralDeep: '#1B2233',

  /* text colour to sit ON a filled accent / bull / bear surface */
  onAcc: '#032015',
  onBull: '#032015',
  onBear: '#2A0410',
} as const;

/** Light theme — overrides the raw palette + washes + shadows only; the
 *  trading semantics and on-* colours resolve through var() and follow.
 *  Emitted into `tokens.css` under `[data-theme='light']`. Contrast on the
 *  light `--s2` is AA for hi/mid/acc; bull/bear/warn are darkened for text. */
export const lightPalette = {
  void: '#E9EDF3', s1: '#FFFFFF', s2: '#F7F9FC', s3: '#EDF1F7', s4: '#E0E7F1',
  line: '#E4E8F0', line2: '#D4DBE6', line3: '#B4BFCF',
  hi: '#121722', mid: '#566072', low: '#8A94A6', dis: '#B4BFCF',
  acc: '#2E8B57', accHi: '#37A468', accDeep: '#DCEDE3',
  liq: '#D62A1C', level: '#7C8AA3',
  bull: '#0C9E60', bear: '#DA2F63', warn: '#B9760E', violet: '#6A57DE', info: '#0B7FB5',
  bullDeep: '#D6F1E4', bearDeep: '#FADCE6', warnDeep: '#FAECD1', violetDeep: '#E4E1FA', neutralDeep: '#E4E8F0',
  onAcc: '#FFFFFF', onBull: '#FFFFFF', onBear: '#FFFFFF',
} as const;

export const lightWash = {
  bull: 'rgba(12,158,96,.14)', bear: 'rgba(218,47,99,.14)',
  bullStrong: 'rgba(12,158,96,.26)', bearStrong: 'rgba(218,47,99,.26)',
  warn: 'rgba(185,118,14,.16)', acc: 'rgba(46,139,87,.12)', accBorder: 'rgba(46,139,87,.5)',
  violet: 'rgba(106,87,222,.14)', liq: 'rgba(214,42,28,.16)',
  hover: 'rgba(20,40,90,.05)', scrim: 'rgba(20,28,40,.38)',
} as const;

/** Alpha washes. The only place rgba() is legal: depth bars, flashes, focus rings. */
export const wash = {
  bull: 'rgba(44,245,167,.14)',
  bear: 'rgba(255,92,135,.14)',
  bullStrong: 'rgba(44,245,167,.28)',
  bearStrong: 'rgba(255,92,135,.28)',
  warn: 'rgba(255,184,77,.14)',
  acc: 'rgba(44,245,167,.12)',
  accBorder: 'rgba(44,245,167,.45)',
  violet: 'rgba(139,124,255,.14)',
  liq: 'rgba(255,58,46,.18)',
  hover: 'rgba(150,175,225,.05)', // ports A's .hover-row verbatim
  scrim: 'rgba(3,5,10,.62)', // modal backdrop
} as const;

/* ────────────────────────────────────────────────── TRADING SEMANTICS ── */
/** Every trading concept resolves to a token. No ad-hoc hex at call sites (task §3). */
export const trading = {
  bid: palette.bull,
  ask: palette.bear,
  bidDepth: wash.bull,
  askDepth: wash.bear,
  long: palette.bull,
  short: palette.bear,
  pnlPos: palette.bull,
  pnlNeg: palette.bear,
  pnlFlat: palette.mid,
  ordLimit: palette.acc, // resting, passive → accent (machine state, not direction)
  ordMarket: palette.hi, // immediate → neutral bright
  ordStop: palette.warn, // conditional → amber
  lineEntry: palette.hi,
  lineSl: palette.bear,
  lineTp: palette.bull,
  lineLiq: palette.liq,
  level: palette.level,
  situation: palette.violet,
  alert: palette.warn,
  connOk: palette.bull,
  connSlow: palette.warn, // reconnecting / stale
  connDown: palette.bear,
} as const;

/* ───────────────────────────────────────────────────────── TYPOGRAPHY ── */
/** Inter + JetBrains Mono, self-hosted (task: no Google CDN). Audit §4.4: both
 *  projects already run Inter; A's Geist declaration was dead. */
export const typography = {
  fontUi: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontMono: "'JetBrains Mono', ui-monospace, 'SF Mono', monospace",
  /* 12px base is B's density (audit §4.4) and the floor for an 8-panel layout. */
  size: { micro: '9px', xs: '10px', sm: '11px', base: '12px', md: '13px', lg: '15px', xl: '18px', xxl: '24px' },
  weight: { regular: 400, medium: 500, semi: 600, bold: 700 },
  lh: { tight: 1.15, base: 1.4, relaxed: 1.55 },
  /* .lbl — micro caps label, ported from B tokens.css */
  labelTracking: '.06em',
  numTracking: '-.02em',
} as const;

/* ──────────────────────────────────────────────── SPACING / DENSITY ── */
/** Two density modes (audit §8 q6 → answered: give the switch, default compact). */
export const density = {
  compact: {
    sp0: '0px', sp1: '2px', sp2: '4px', sp3: '6px', sp4: '8px', sp5: '12px', sp6: '16px', sp7: '24px',
    rowLadder: '18px',
    rowTape: '16px',
    rowTable: '22px',
    rowJournal: '24px',
    panelHeader: '22px',
    tabH: '22px',
    ctrlSm: '20px',
    ctrlMd: '24px',
    titlebar: '30px',
    statusbar: '22px',
  },
  comfortable: {
    sp0: '0px', sp1: '3px', sp2: '6px', sp3: '8px', sp4: '12px', sp5: '16px', sp6: '20px', sp7: '32px',
    rowLadder: '22px',
    rowTape: '20px',
    rowTable: '28px',
    rowJournal: '30px',
    panelHeader: '26px',
    tabH: '26px',
    ctrlSm: '24px', // ≥24px hit target requirement holds in comfortable
    ctrlMd: '28px',
    titlebar: '32px',
    statusbar: '24px',
  },
} as const;

/* ─────────────────────────────────────────────── SHAPE / SHADOW / Z ── */
export const radius = {
  ctrl: '3px', // buttons, panels, tabs  — compromise between A's 14 and B's 2 (audit §4.11)
  input: '4px',
  panel: '3px',
  modal: '14px', // softer — the site's floating-surface feel (owner 2026-09-10)
  pill: '2px',
} as const;

/** Shadows exist ONLY on floating layers. Docked panels are flat 1px — a shadow on
 *  eight tiled panels is both wrong (fake depth in a tiling layout) and GPU-expensive. */
export const shadow = {
  float: '0 18px 50px rgba(0,0,0,.55), 0 0 0 1px rgba(150,175,225,.08)',
  dropdown: '0 10px 28px rgba(0,0,0,.5), 0 0 0 1px rgba(150,175,225,.07)',
  ghost: '0 20px 44px rgba(0,0,0,.6)',
  none: 'none',
} as const;

export const blurFloat = 'blur(14px)'; // modals, palette, tooltips, ctx menu, ghost, popout preview only

export const motion = {
  durFast: '120ms', // hover, press, tab underline
  durBase: '160ms', // panel open, dropdown, toast in
  durSlow: '240ms', // modal in, popout preview
  durSlower: '300ms', // modal / notification entrance — the site's feel is the canon (unification decision 2026-09-09)
  easeOut: 'cubic-bezier(.2,.7,.3,1)',
  /* The spring lives on FLOATING surfaces (modal, toast, dropdown, ticket) — the
     site's motion is the canon for those. `easeWin` keeps the same curve under
     its own name for the one profit-close celebration. */
  easeSpring: 'cubic-bezier(.3,1.36,.44,1)',
  easeWin: 'cubic-bezier(.3,1.36,.44,1)',
} as const;

export const z = {
  panel: 1,
  overlay: 100, // chart overlays, ladder order overlay
  dropdown: 200, // select, context menu, tooltip
  modal: 300,
  toast: 400,
  dragghost: 500,
} as const;

/* ─────────────────────────────────────────────────────────── EMITTER ── */

const kebab = (s: string) => s.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());

function block(map: Record<string, string | number>, prefix: string): string {
  return Object.entries(map).map(([k, v]) => `  --${prefix}${kebab(k)}: ${v};`).join('\n');
}

/** Single emitter. Everything CSS knows about colour comes from here. */
export function cssVars(): string {
  return `/* GENERATED FROM tokens.ts — do not edit by hand */
:root {
${block(palette, '')}
${block(wash, 'w-')}
${block(trading, 't-')}
  --font-ui: ${typography.fontUi};
  --font-mono: ${typography.fontMono};
${block(typography.size, 'fs-')}
${block(typography.lh, 'lh-')}
${block(radius, 'r-')}
${block(shadow, 'shadow-')}
  --blur-float: ${blurFloat};
${block(motion, '')}
${block(z, 'z-')}
}
:root, [data-density='compact'] {
${block(density.compact, '')}
}
[data-density='comfortable'] {
${block(density.comfortable, '')}
}
/* Light theme — see lightPalette / lightWash above. Hand-maintained in
   tokens.css to keep the diff readable; keep the two in sync. */
[data-theme='light'] {
${block(lightPalette, '')}
${block(lightWash, 'w-')}
}
@media (prefers-reduced-motion: reduce) {
  :root {
    --dur-fast: 1ms; --dur-base: 1ms; --dur-slow: 1ms; --dur-slower: 1ms;
    --ease-win: linear; --ease-spring: linear;
  }
}`;
}

export type Density = keyof typeof density;
export const tokens = { palette, wash, trading, typography, density, radius, shadow, motion, z, blurFloat };
export default tokens;
